import { useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useFreebets } from '@hooks/queries/useFreebets'
import { syncFreebetList } from '@slices/bettingSlice'

// The betting panel only ever stakes ACTIVE freebets; history/expired live in
// the menu section. page/size are sent null so ROOT returns EVERY active freebet
// (no paging) — the panel needs the whole list to swap through.
const ACTIVE_STATUS = ['ACTIVE']

/**
 * The SINGLE place the server freebet list is mirrored into the store. Mount it
 * once (in the freebet controller). Everything downstream — the two cards, the
 * arrows, the reconcile — reads the slice, so there is exactly one source of
 * truth for the list (the trap both old implementations missed by keeping the
 * list in component state and reconciling in effects). The orchestrator triggers
 * a refetch via invalidate(['freebets']) after placement/settlement; that flows
 * back through here, keeping the whole loop unidirectional.
 */
export const useFreebetsSync = () => {
  const dispatch = useDispatch()
  const dualMode = useSelector((state) => Boolean(state.layout?.duelBetMode?.freebets))
  const { data, isLoading } = useFreebets({ status: ACTIVE_STATUS, page: null, pageSize: null })

  // `data` (and thus `data.rows`) is referentially stable between renders while
  // the underlying freebet data is unchanged — the registry's `select` is
  // memoized on the (stable) formatters — so this effect fires only on an actual
  // list change, never every render. Reconcile happens atomically in the reducer.
  const rows = data?.rows

  useEffect(() => {
    dispatch(syncFreebetList({ rows: rows ?? [], dualMode }))
  }, [rows, dualMode, dispatch])

  // isLoading = the initial fetch (no cached data yet). Refetches after placement
  // keep the current list on screen (keepPreviousData), so no loader flash.
  return { isLoading }
}
